import React, { useMemo, useCallback } from 'react';
import { Lead } from '../../../types';
import { useCrmData } from '../../../contexts/CrmProviders';
import DataTable, { Column } from '../../../components/shared/DataTable';
import ActionMenu, { createEditAction, createDeleteAction, createDuplicateAction } from '../../../components/shared/ActionMenu';
import { LeadStageCell, LeadValueCell, LeadAgentCell } from './LeadTableCells';
import { formatDateTime } from '../../../utils/formatters';
import Pagination from '../../../components/shared/Pagination';
import { leadHistoryService } from '../../../services/leadHistoryService';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { WhatsAppIcon, MetaLogoIcon } from '../../../components/icons/CustomIcons';
import { AppIcon } from '../../../components/icons/AppIcon';
import { cn } from '@/lib/utils';

interface PaginationProps {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  selectedCount?: number;
}

interface LeadListViewProps {
  leads: Lead[];
  onLeadClick: (lead: Lead) => void;
  onDelete?: (lead: Lead) => void;
  onDuplicate?: (lead: Lead) => void;
  showCheckbox?: boolean;
  selectedIds?: Set<string>;
  onToggleSelection?: (id: string) => void;
  onToggleAll?: () => void;
  pagination?: PaginationProps;
  allSelected?: boolean;
  someSelected?: boolean;
  groupByOrigin?: boolean;
  onGroupByOriginChange?: (grouped: boolean) => void;
}

const LeadListView: React.FC<LeadListViewProps> = ({
  leads,
  onLeadClick,
  onDelete,
  onDuplicate,
  showCheckbox = false,
  selectedIds = new Set(),
  onToggleSelection,
  onToggleAll,
  pagination,
  allSelected,
  someSelected,
  groupByOrigin = false,
  onGroupByOriginChange,
}) => {
  const { funnelStages, crmUsers, saveLead } = useCrmData();
  const { currentOrganization } = useOrganization();

  // Lógica de atualização com registro de histórico
  const handleUpdateLead = useCallback(async (lead: Lead, field: keyof Lead, value: any) => {
    const finalValue = (field === 'value') ? parseFloat(value) || 0 : value;
    const orgId = currentOrganization?.id;

    // Registrar histórico antes de salvar
    if (orgId) {
      if (field === 'stage' && value !== lead.stage) {
        const oldStage = funnelStages.find(s => s.id === lead.stage);
        const newStage = funnelStages.find(s => s.id === value);
        leadHistoryService.addEvent({
          leadId: lead.id,
          organizationId: orgId,
          type: 'stage_changed',
          description: 'Etapa alterada (via lista)',
          oldValue: oldStage?.name || lead.stage,
          newValue: newStage?.name || value
        }).catch(() => { });
      } else if (field === 'value' && Math.abs(lead.value - finalValue) > 0.01) {
        leadHistoryService.addEvent({
          leadId: lead.id,
          organizationId: orgId,
          type: 'value_updated',
          description: 'Valor atualizado (via lista)',
          oldValue: `R$ ${lead.value.toFixed(2)}`,
          newValue: `R$ ${finalValue.toFixed(2)}`
        }).catch(() => { });
      } else if (field === 'agent' && lead.agent !== finalValue) {
        leadHistoryService.addEvent({
          leadId: lead.id,
          organizationId: orgId,
          type: finalValue ? 'agent_assigned' : 'agent_removed',
          description: finalValue ? 'Atendente atribuído (via lista)' : 'Atendente removido (via lista)',
          oldValue: lead.agent || undefined,
          newValue: finalValue || undefined
        }).catch(() => { });
      }
    }

    if (field === 'stage' && (value === 'won' || value === 'lost')) {
      // Mantém a etapa original para o funil conseguir filtrar corretamente
      const updated = { ...lead, status: value as 'won' | 'lost', stage: lead.stage };
      await saveLead(updated);
      return;
    }

    if (field === 'stage' && value !== 'won' && value !== 'lost') {
      await saveLead({ ...lead, stage: finalValue, status: 'open' as const });
      return;
    }

    await saveLead({ ...lead, [field]: finalValue });
  }, [saveLead, currentOrganization?.id, funnelStages]);

  // Definição das colunas
  const columns: Column<Lead>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Cliente',
      render: (lead) => (
        <button
          onClick={(e) => { e.stopPropagation(); onLeadClick(lead); }}
          className="text-left font-medium text-default transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          {lead.name}
        </button>
      )
    },
    {
      key: 'stage',
      header: 'Etapa do Funil',
      width: '200px',
      render: (lead) => (
        <LeadStageCell
          lead={lead}
          funnelStages={funnelStages}
          onUpdate={handleUpdateLead}
        />
      )
    },
    {
      key: 'value',
      header: 'Valor',
      width: '150px',
      render: (lead) => (
        <LeadValueCell
          lead={lead}
          onUpdate={handleUpdateLead}
        />
      )
    },
    {
      key: 'origin',
      label: 'Origem',
      header: (
        <button
          type="button"
          aria-pressed={groupByOrigin}
          aria-label={groupByOrigin ? 'Remover agrupamento por origem' : 'Agrupar por origem'}
          onClick={(e) => {
            e.stopPropagation();
            onGroupByOriginChange?.(!groupByOrigin);
          }}
          className={cn(
            'inline-flex min-h-11 items-center gap-s1 rounded-md text-inherit',
            'hover:text-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
            'md:min-h-0',
          )}
        >
          <span>Origem</span>
          <AppIcon
            name={groupByOrigin ? 'arrow-down' : 'arrow-up'}
            className={cn('size-3.5 shrink-0', groupByOrigin ? 'text-primary' : 'text-muted')}
          />
        </button>
      ),
      render: (lead) => {
        const origin = lead.origin?.toLowerCase() || '';
        if (origin.includes('whatsapp')) {
          return <WhatsAppIcon className="w-5 h-5" />;
        }
        if (origin.includes('meta')) {
          return <MetaLogoIcon className="w-5 h-5" />;
        }
        return <span className="text-default">{lead.origin || '-'}</span>;
      }
    },
    {
      key: 'agent',
      header: 'Atendente',
      width: '180px',
      render: (lead) => (
        <LeadAgentCell
          lead={lead}
          crmUsers={crmUsers}
          onUpdate={handleUpdateLead}
        />
      )
    },
    {
      key: 'entryDate',
      header: 'Entrada',
      render: (lead) => (
        <time
          dateTime={lead.entryDate}
          className="font-mono text-sm font-normal tabular-nums text-muted"
        >
          {formatDateTime(lead.entryDate)}
        </time>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      align: 'right',
      render: (lead) => (
        <ActionMenu actions={[
          createEditAction(() => onLeadClick(lead)),
          ...(onDuplicate ? [createDuplicateAction(() => onDuplicate(lead))] : []),
          ...(onDelete ? [createDeleteAction(() => onDelete(lead))] : []),
        ]} />
      )
    }
  ], [funnelStages, crmUsers, handleUpdateLead, onLeadClick, onDuplicate, onDelete, groupByOrigin, onGroupByOriginChange]);

  return (
    <div className="min-w-0 w-full">
      <DataTable
        columns={columns}
        data={leads}
        getRowKey={(lead) => lead.id}
        emptyMessage="Nenhum lead encontrado com os filtros aplicados."
        selectable={showCheckbox}
        selectedIds={selectedIds}
        onToggleSelection={onToggleSelection}
        onToggleAll={onToggleAll}
        allSelected={allSelected}
        someSelected={someSelected}
        getRowClassName={() => 'cursor-pointer'}
        footer={
          pagination ? (
            <Pagination
              currentPage={pagination.currentPage}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              onPageChange={pagination.onPageChange}
              selectedCount={pagination.selectedCount}
            />
          ) : undefined
        }
      />
    </div>
  );
};

export default LeadListView;
